import { FC, forwardRef, HTMLProps, ReactElement } from 'react';
import { cx } from '@emotion/css';
import { Label } from '../label/label';
import { getHintBorderColor, Hint } from '../hint';
import { TooltipProps } from '../../tooltip/tooltip';
import { HintProps } from '../hint/hint.types';

const DEFAULT_MAX_CHARACTERS = 100;

export interface InputFieldIconProps {
  icon?: string;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  className?: string;
}

export type InputFieldProps = HTMLProps<HTMLInputElement> & {
  description?: string;
  hint?: HintProps;
  icon?: string;
  iconClickAction?: () => void;
  iconPosition?: 'left' | 'right';
  validateOnSubmit?: boolean;
  classNameInput?: string;
  tooltip?: ReactElement<TooltipProps>;
  displayCharsCounter?: boolean;
  charsCounter?: number;
  SrOnlyComponent?: ReactElement;
};

export const InputFieldIcon = ({ icon, iconPosition, disabled, className }: InputFieldIconProps) => {
  const getIconFilter = () => {
    return cx(
      'saturate(0%)',
      disabled
        ? 'invert(76%) sepia(5%) hue-rotate(269deg) brightness(94%) contrast(87%)'
        : 'invert(11%) sepia(0%) hue-rotate(219deg) brightness(100%) contrast(90%)'
    );
  };

  return (
    <img
      alt=""
      src={icon}
      className={cx('absolute top-[calc(50%-0.5rem)] h-4 w-4', iconPosition === 'right' ? 'right-4' : 'left-4', className)}
      style={{
        filter: getIconFilter(),
      }}
    />
  );
};

export const InputField: FC<React.PropsWithChildren<InputFieldProps>> = forwardRef(
  (
    {
      description,
      label,
      hint,
      disabled = false,
      icon,
      iconPosition = 'right',
      iconClickAction,
      type = 'text',
      className = '',
      classNameInput = '',
      tooltip,
      displayCharsCounter = false,
      charsCounter = 0,
      SrOnlyComponent,
      ...inputProps
    },
    ref
  ) => (
    <div className={cx(className)}>
      <label className="stihl-text-body-default-reg">
        {label && (
          <div className="mb-1 flex flex-row items-center gap-2 stihl-text-body-sm-reg">
            {label && <Label disabled={disabled} text={label} />}
            {tooltip}
          </div>
        )}
        {description && <div className="mb-1 stihl-text-body-xs-reg">{description}</div>}
        <div className="relative h-12">
          <input
            className={cx(
              'inline-block h-12 w-full',
              !disabled ? getHintBorderColor(hint?.type) : '!border-secondary !bg-secondary-light text-secondary',
              {
                'pr-11': !!icon && iconPosition === 'right',
                'pl-11': !!icon && iconPosition === 'left',
              },
              classNameInput
            )}
            type={type}
            {...inputProps}
            disabled={disabled}
            ref={ref}
          />

          {icon && !iconClickAction && <InputFieldIcon icon={icon} iconPosition={iconPosition} disabled={disabled} />}

          {icon && iconClickAction && (
            <button
              type="button"
              onClick={iconClickAction}
              className="absolute right-0 h-[48px] w-[48px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-black"
            >
              <InputFieldIcon icon={icon} iconPosition={iconPosition} disabled={disabled} className="top-4" />
              {SrOnlyComponent}
            </button>
          )}
        </div>
        {displayCharsCounter && (
          <span className={cx('absolute right-0 text-secondary-dark')}>
            {charsCounter}/{inputProps?.maxLength || DEFAULT_MAX_CHARACTERS}
          </span>
        )}
        {!!hint?.message && <Hint {...hint} />}
      </label>
    </div>
  )
);
